import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Users,
  FileText,
  ExternalLink,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import lhgLogo from "@/assets/brand/lhg_mark_color.png";

const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

function LeadsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/leads");
        setRows(data);
      } catch (e) {
        toast.error("Could not load leads.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading)
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="animate-spin text-blue" size={22} />
      </div>
    );

  if (rows.length === 0)
    return (
      <p data-testid="leads-empty" className="font-body text-charcoal/60 py-16 text-center">
        No leads yet. Contact submissions and PDF downloads will appear here.
      </p>
    );

  return (
    <div className="overflow-x-auto border border-blue/12 bg-white">
      <Table data-testid="leads-table">
        <TableHeader>
          <TableRow className="bg-blue/[0.04]">
            <TableHead className="text-blue">Date</TableHead>
            <TableHead className="text-blue">Name</TableHead>
            <TableHead className="text-blue">Email</TableHead>
            <TableHead className="text-blue">Phone</TableHead>
            <TableHead className="text-blue">Years</TableHead>
            <TableHead className="text-blue">Website</TableHead>
            <TableHead className="text-blue">Source</TableHead>
            <TableHead className="text-blue">Detail</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid="lead-row" className="font-body text-sm">
              <TableCell className="whitespace-nowrap text-charcoal/70">{fmtDate(r.created_at)}</TableCell>
              <TableCell className="font-semibold text-blue whitespace-nowrap">{r.name}</TableCell>
              <TableCell>
                <a href={`mailto:${r.email}`} className="text-blue hover:text-amber-deep underline-offset-2 hover:underline">
                  {r.email}
                </a>
              </TableCell>
              <TableCell className="whitespace-nowrap text-charcoal/70">{r.phone || "-"}</TableCell>
              <TableCell className="text-charcoal/70">{r.years_in_business || "-"}</TableCell>
              <TableCell>
                {r.website ? (
                  <a
                    href={r.website.startsWith("http") ? r.website : `https://${r.website}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-blue hover:text-amber-deep"
                  >
                    {r.website} <ExternalLink size={12} />
                  </a>
                ) : (
                  <span className="text-charcoal/40">-</span>
                )}
              </TableCell>
              <TableCell>
                <Badge
                  className={
                    r.type === "Download"
                      ? "bg-amber-deep/15 text-amber-deep hover:bg-amber-deep/15"
                      : "bg-blue/12 text-blue hover:bg-blue/12"
                  }
                >
                  {r.type}
                </Badge>
              </TableCell>
              <TableCell className="max-w-[220px] text-charcoal/70">
                <span className="line-clamp-2">{r.detail || r.message || "-"}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const EMPTY_POST = { title: "", excerpt: "", body: "", published: true };

function InsightsTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // post object or {new:true}
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data);
    } catch (e) {
      toast.error("Could not load posts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setForm(EMPTY_POST);
    setEditing({ new: true });
  };
  const openEdit = (p) => {
    setForm({ title: p.title, excerpt: p.excerpt || "", body: p.body, published: p.published });
    setEditing(p);
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing?.new) {
        await api.post("/admin/posts", form);
        toast.success("Post published.");
      } else {
        await api.put(`/admin/posts/${editing.id}`, form);
        toast.success("Post updated.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Could not save the post.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/posts/${toDelete.id}`);
      toast.success("Post deleted.");
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error("Could not delete the post.");
    }
  };

  if (loading)
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="animate-spin text-blue" size={22} />
      </div>
    );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="font-body text-charcoal/70 text-sm">{posts.length} post(s)</p>
        <Button data-testid="new-post-btn" onClick={openNew} className="bg-blue hover:bg-blue-deep text-cream rounded-none">
          <Plus size={16} className="mr-1" /> New post
        </Button>
      </div>

      {posts.length === 0 ? (
        <p className="font-body text-charcoal/60 py-16 text-center">
          No posts yet. Create your first insight.
        </p>
      ) : (
        <div className="border border-blue/12 bg-white divide-y divide-blue/10">
          {posts.map((p) => (
            <div key={p.id} data-testid="post-row" className="flex items-center justify-between gap-4 p-5">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <h3 className="font-serif font-bold text-lg text-blue truncate">{p.title}</h3>
                  <Badge
                    className={
                      p.published
                        ? "bg-green-600/15 text-green-700 hover:bg-green-600/15"
                        : "bg-charcoal/10 text-charcoal/60 hover:bg-charcoal/10"
                    }
                  >
                    {p.published ? "Published" : "Draft"}
                  </Badge>
                </div>
                <p className="font-body text-sm text-charcoal/60 mt-1">Updated {fmtDate(p.updated_at)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  data-testid="edit-post-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => openEdit(p)}
                  className="rounded-none border-blue/30 text-blue"
                >
                  <Pencil size={15} />
                </Button>
                <Button
                  data-testid="delete-post-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setToDelete(p)}
                  className="rounded-none border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={15} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="post-editor" className="bg-cream border-blue/20 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif font-bold text-2xl text-blue">
              {editing?.new ? "New insight" : "Edit insight"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-1">
            <div>
              <Label className="eyebrow text-[0.56rem] text-slate">Title</Label>
              <Input
                data-testid="post-title"
                className="mt-2 rounded-none border-blue/25 bg-white"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="A short, strategic title"
              />
            </div>
            <div>
              <Label className="eyebrow text-[0.56rem] text-slate">Excerpt</Label>
              <Input
                data-testid="post-excerpt"
                className="mt-2 rounded-none border-blue/25 bg-white"
                value={form.excerpt}
                onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                placeholder="One line shown in the list (optional)"
              />
            </div>
            <div>
              <Label className="eyebrow text-[0.56rem] text-slate">Body</Label>
              <Textarea
                data-testid="post-body"
                rows={9}
                className="mt-2 rounded-none border-blue/25 bg-white resize-none"
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                placeholder="Write your article. Blank lines create new paragraphs."
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                data-testid="post-published"
                checked={form.published}
                onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))}
              />
              <span className="font-body text-sm text-charcoal/80">
                {form.published ? "Published (visible on site)" : "Draft (hidden)"}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="save-post-btn"
              onClick={save}
              disabled={saving}
              className="bg-blue hover:bg-blue-deep text-cream rounded-none w-full sm:w-auto"
            >
              {saving ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
              {editing?.new ? "Publish" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="bg-cream border-blue/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-blue">Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.title}" will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={confirmDelete}
              className="rounded-none bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-blue text-cream">
        <div className="container-editorial flex items-center justify-between py-5">
          <div className="flex items-center gap-4">
            <img src={lhgLogo} alt="LHG" className="h-11 w-auto object-contain bg-cream/95 p-1" />
            <div>
              <p className="eyebrow text-amber-bright text-[0.55rem]">Private Dashboard</p>
              <p className="font-serif font-bold text-xl leading-none mt-1">Logan Hunter Group</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-body text-sm text-cream/70">{user?.email}</span>
            <Button
              data-testid="logout-btn"
              onClick={doLogout}
              variant="outline"
              className="rounded-none border-cream/30 text-cream hover:bg-cream/10 hover:text-cream"
            >
              <LogOut size={15} className="mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="container-editorial py-10 md:py-14">
        <Tabs defaultValue="leads">
          <TabsList className="bg-blue/[0.06] rounded-none mb-8">
            <TabsTrigger data-testid="tab-leads" value="leads" className="rounded-none data-[state=active]:bg-blue data-[state=active]:text-cream">
              <Users size={15} className="mr-2" /> Leads
            </TabsTrigger>
            <TabsTrigger data-testid="tab-insights" value="insights" className="rounded-none data-[state=active]:bg-blue data-[state=active]:text-cream">
              <FileText size={15} className="mr-2" /> Insights
            </TabsTrigger>
          </TabsList>
          <TabsContent value="leads">
            <LeadsTab />
          </TabsContent>
          <TabsContent value="insights">
            <InsightsTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
